import {
  Checkbox,
  Heading,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelSection,
  Stack,
  Strong,
} from '@customerio/pluma-components/react';
import type { MergeConfirmations } from '../types';

type MergeConfirmationsPanelProps = {
  confirmations: MergeConfirmations;
  /** The viewer may edit people; without it the statements cannot be agreed to. */
  canMerge: boolean;
  /** The workspace's word for a campaign ("automation" once renamed). */
  campaignTerm: string;
  /** The workspace's word for one person ("profile" once renamed). */
  personTerm: string;
  /** The workspace's word for several people ("profiles" once renamed). */
  peopleTerm: string;
  onChange: (confirmations: MergeConfirmations) => void;
};

/** The three statements that must be agreed to before "Confirm and merge" is enabled. */
export function MergeConfirmationsPanel({
  confirmations,
  canMerge,
  campaignTerm,
  personTerm,
  peopleTerm,
  onChange,
}: MergeConfirmationsPanelProps) {
  const set = (key: keyof MergeConfirmations) => (checked: boolean) => onChange({ ...confirmations, [key]: checked });

  return (
    <Panel backgroundColor="subtle">
      <PanelSection>
        <InlineStack alignBlock="center" mb="150" gap="050">
          <Icon name="caution" color="critical" />
          <Heading level="3" text="product-h4">
            Merging {peopleTerm} is permanent
          </Heading>
        </InlineStack>
        <Stack gap="100">
          <Checkbox
            name="merge_confirmation_1"
            value="merge_confirmation_1"
            isDisabled={!canMerge}
            isChecked={confirmations.segments}
            onCheckedChange={set('segments')}
            label={
              <>
                I understand that merging {peopleTerm} could add this {personTerm} to new segments and trigger messages.{' '}
                <Link href="https://docs.customer.io/merge-people" variant="secondary" isExternal>
                  Learn more
                </Link>
              </>
            }
          />
          <Checkbox
            name="merge_confirmation_2"
            value="merge_confirmation_2"
            isDisabled={!canMerge}
            isChecked={confirmations.history}
            onCheckedChange={set('history')}
            label={
              <>
                However, I understand that the primary {personTerm} also inherits {campaignTerm} and delivery history
                from the secondary {personTerm}.{' '}
                <Link href="https://docs.customer.io/merge-people#segment-rules" variant="secondary" isExternal>
                  Learn more
                </Link>
              </>
            }
          />
          <Checkbox
            name="merge_confirmation_3"
            value="merge_confirmation_3"
            isDisabled={!canMerge}
            isChecked={confirmations.irreversible}
            onCheckedChange={set('irreversible')}
            label={
              <>
                I understand that I <Strong>can't</Strong> restore the original {peopleTerm} after I merge them.
              </>
            }
          />
        </Stack>
      </PanelSection>
    </Panel>
  );
}
